/**
 * In-app notification center service.
 *
 * Server is the source of truth when `GET /api/notifications` (or a probed
 * alias) responds. Session in-memory store (`notification-inbox.js`) holds the
 * current Vuex/FCM copy only — no localStorage persistence / stale fallback.
 *
 * Preferred model: backend inserts `notifications` rows on ping / escalation /
 * assign / announcement / holiday. Until that ships, the client may POST
 * upserts with `dedupeKey` when it synthesizes an item that has no server row.
 *
 * Aligns with backend-handoff/TASK_MANAGEMENT_API_DOCUMENTATION.md §6.5 / §11.
 */

import axios from 'axios'
import AuthService from './auth-header'
import taskService from './task-service'
import {
  taskId as getTaskId,
  taskAssigneeEmpId,
  taskAssigneeName,
  taskOwnerEmpId,
  getEscalationLevel,
  escalationLevelMeta,
  taskRecurringTaskId,
  isTaskComplete,
  taskEligibleForRecurringReminder,
  isStaleRecurringTaskReminderNotification,
  TASK_STATUSES,
  normalizeTaskStatus,
} from '@/utils/task-config'
import {
  getTaskPing,
  getEmployeePing,
} from '@/utils/task-ping-store'
import {
  listNotifications,
  upsertNotifications,
  upsertNotification,
  markNotificationRead,
  markAllNotificationsRead,
  clearAllNotifications,
  replaceFromApi,
} from '@/utils/notification-inbox'
import { FCM_PUSH_ONLY, isInAppInboxEnabledForType, isTaskManagementPush } from '@/utils/fcm-push'
import { canAccessTaskManagement } from '@/utils/task-permissions'

function resolveNotificationsApiUrl() {
  if (process.env.NODE_ENV === "production") {
      return 'https://ats.sattvahuman.in/api/'
  } else {
      return 'http://192.168.0.30:5000/api/'
  }
}

const API_URL = resolveNotificationsApiUrl()

/** One soft warning per session when the notifications host is unreachable. */
let networkSoftWarned = false
/** One soft warning per session for 401/403 (avoids poll spam). */
let authSoftWarned = false
/**
 * When true, skip further notification API GETs until
 * {@link resetNotificationApiAuthGate} (login / in-app navigation).
 */
let apiAuthBlocked = false

function softWarnNetwork(prefix, err) {
  const httpStatus = err?.response?.status
  if (httpStatus === 401 || httpStatus === 403) {
    if (authSoftWarned) return
    authSoftWarned = true
    console.warn(`${prefix} (auth soft-fail, retries paused):`, httpStatus)
    return
  }
  const msg = (err && err.message) || String(err || 'unknown error')
  const isNetwork = /timeout|Network Error|ECONNABORTED|ERR_NETWORK|ENOTFOUND|ECONNREFUSED/i.test(msg)
    || err?.code === 'ECONNABORTED'
    || err?.code === 'ERR_NETWORK'
  if (isNetwork) {
    if (networkSoftWarned) return
    networkSoftWarned = true
    console.warn(`${prefix} unreachable (soft-fail):`, msg)
    return
  }
  // Axios 401 message without response object (rare) — still de-dupe.
  if (/status code 401|status code 403/i.test(msg)) {
    if (authSoftWarned) return
    authSoftWarned = true
    console.warn(`${prefix} (auth soft-fail, retries paused):`, msg)
    return
  }
  console.warn(`${prefix}:`, msg)
}

function markNotificationsAuthBlocked() {
  apiAuthBlocked = true
  apiListPath = null
}

/**
 * Clear auth gate so the next inbox load can probe again (e.g. after login
 * or in-app navigation). Pass `{ keepWarnFlags: true }` to avoid re-spamming
 * console on repeated resume attempts that still 401.
 */
export function resetNotificationApiAuthGate(options = {}) {
  apiAuthBlocked = false
  apiProbeDone = false
  apiListPath = null
  if (!options.keepWarnFlags) {
    authSoftWarned = false
  }
}

/** Candidate list endpoints — first 2xx with a recognizable payload wins. */
const LIST_CANDIDATES = [
  'notifications',
  'notifications/me',
  'user/notifications',
  'users/notifications',
  'pm/notifications',
]

let apiListPath = null
let apiProbeDone = false
/** Set of dedupeKeys already POSTed this session (avoid spam). */
const postedDedupeKeys = new Set()
/**
 * Session set of Done/completed task ids — used to hide assignment /
 * escalation / ping / reminder noise for work that is already finished.
 * Accumulates across polls so archived completed rows still suppress
 * stale inbox items after they drop off `GET /pm/tasks`.
 */
const completedTaskIds = new Set()

/** Pre-completion task inbox types that must not linger after Done. */
function isPreCompletionTaskInboxType(type) {
  const t = String(type || '').toLowerCase()
  if (!t.startsWith('task_')) return false
  // Completion notices for the manager should remain visible.
  if (t === 'task_completed') return false
  return true
}

function notificationLinkedTaskId(n) {
  if (!n) return null
  const meta = n.meta || {}
  if (meta.taskId != null && meta.taskId !== '') return String(meta.taskId)
  if (n.linkQuery && n.linkQuery.taskId != null && n.linkQuery.taskId !== '') {
    return String(n.linkQuery.taskId)
  }
  const link = typeof n.link === 'string' ? n.link : ''
  const m = link.match(/[?&]taskId=([^&]+)/i)
  if (m) {
    try {
      return decodeURIComponent(m[1])
    } catch (e) {
      return m[1]
    }
  }
  return null
}

/**
 * Update {@link completedTaskIds} from a live task list (Done → add;
 * reopened → remove).
 */
function rememberTaskCompletionState(tasks = []) {
  ;(tasks || []).forEach(task => {
    const id = getTaskId(task)
    if (id == null) return
    const key = String(id)
    if (isTaskComplete(task)) completedTaskIds.add(key)
    else completedTaskIds.delete(key)
  })
}

/**
 * Immediately mark a task Done for inbox filtering (call when the board
 * flips status → Done so the bell updates before the next poll).
 */
export function noteTaskCompletedForInbox(taskOrId) {
  const id = taskOrId != null && typeof taskOrId === 'object'
    ? getTaskId(taskOrId)
    : taskOrId
  if (id != null && id !== '') completedTaskIds.add(String(id))
}

function shouldHideCompletedTaskNotification(n) {
  if (!isPreCompletionTaskInboxType(n && n.type)) return false
  const tid = notificationLinkedTaskId(n)
  if (tid != null && completedTaskIds.has(String(tid))) return true
  // Payload hint when task list is not yet available this session.
  const metaStatus = n && n.meta && (n.meta.status ?? n.meta.taskStatus ?? n.meta.task_status)
  if (metaStatus != null && normalizeTaskStatus({ status: metaStatus }) === TASK_STATUSES.DONE) {
    return true
  }
  return false
}

function readUserEmpId() {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || 'null')
    if (!userData) return null
    return userData.emp_id ?? userData.empId ?? null
  } catch (e) {
    return null
  }
}

function readUserId() {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || 'null')
    if (!userData || userData.id == null) return null
    return userData.id
  } catch (e) {
    return null
  }
}

function extractArrayPayload(data) {
  if (!data) return null
  if (Array.isArray(data)) return data
  if (Array.isArray(data.data)) return data.data
  if (Array.isArray(data.notifications)) return data.notifications
  if (data.data && Array.isArray(data.data.notifications)) return data.data.notifications
  if (data.status === 'OK' && Array.isArray(data.data)) return data.data
  return null
}

function extractObjectPayload(data) {
  if (!data || typeof data !== 'object') return null
  if (data.status === 'OK' && data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    return data.data
  }
  if (data.id != null || data._id != null) return data
  if (data.data && (data.data.id != null || data.data._id != null)) return data.data
  return null
}

/**
 * Write base path for create / read-all / clear.
 * `notifications/me` → `notifications`; other list paths used as-is.
 */
function apiWriteBase() {
  if (!apiListPath) return null
  if (apiListPath === 'notifications/me') return 'notifications'
  return apiListPath
}

function normalizeApiItem(raw) {
  if (!raw || typeof raw !== 'object') return null
  const id = raw.id != null
    ? String(raw.id)
    : (raw._id != null ? String(raw._id) : null)
  if (!id) return null
  const meta = raw.meta && typeof raw.meta === 'object' ? { ...raw.meta } : {}
  if (raw.dedupeKey || raw.dedupe_key) {
    meta.dedupeKey = raw.dedupeKey || raw.dedupe_key
  }
  meta.rawId = id
  const read = raw.read === true
    || raw.isRead === true
    || !!raw.readAt
    || !!raw.read_at
  const type = raw.type || raw.category || 'general'
  const isInboundCall = type === 'inbound_call_unavailable' || type === 'inbound_call'
  let link = raw.link || raw.url || raw.href || null
  let linkQuery = raw.linkQuery || raw.query || null
  if (isInboundCall && !link) {
    link = { name: 'apps-call-analytics' }
    linkQuery = { ...(linkQuery || {}), direction: 'inbound' }
  }
  return {
    id: `api:${id}`,
    type,
    title: raw.title || raw.subject || 'Notification',
    message: raw.message || raw.body || raw.subtitle || raw.description || '',
    createdAt: raw.createdAt || raw.created_at || raw.timestamp || raw.date || new Date().toISOString(),
    read,
    link,
    linkQuery,
    icon: raw.icon || (isInboundCall ? 'PhoneIncomingIcon' : 'BellIcon'),
    variant: raw.variant || (isInboundCall ? 'light-warning' : 'light-primary'),
    source: 'api',
    meta,
  }
}

/**
 * Probe once per session for a backend notification list. Soft-fails so a
 * missing route never breaks the navbar. 401/403 stops the probe and blocks
 * further list GETs until {@link resetNotificationApiAuthGate}.
 */
async function probeApiList() {
  if (apiAuthBlocked) return null
  if (apiProbeDone) return apiListPath
  apiProbeDone = true

  for (let i = 0; i < LIST_CANDIDATES.length; i += 1) {
    const path = LIST_CANDIDATES[i]
    try {
      const res = await axios.get(API_URL + path, {
        headers: AuthService.authHeader(),
        validateStatus: status => status < 500,
        timeout: 8000,
      })
      if (res.status === 401 || res.status === 403) {
        softWarnNetwork('[notifications] probe failed', { response: { status: res.status } })
        markNotificationsAuthBlocked()
        apiProbeDone = true
        return null
      }
      if (res.status === 200 && extractArrayPayload(res.data) != null) {
        apiListPath = path
        return apiListPath
      }
    } catch (e) {
      softWarnNetwork('[notifications] probe failed', e)
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        markNotificationsAuthBlocked()
        return null
      }
      // try next candidate (network soft-warn once)
    }
  }
  apiListPath = null
  return null
}

/**
 * Resolve a human assignee name for inbox copy.
 * Prefer task.assignedToName / nested assignee fields; fall back to an
 * empId→name map built from the current task batch (or an optional map).
 */
function resolveAssigneeDisplayName(task, nameMap = null) {
  if (!task) return null
  const empId = taskAssigneeEmpId(task)
  const fromTask = taskAssigneeName(task)
  if (
    fromTask
    && fromTask !== '-'
    && fromTask !== 'Unassigned'
    && String(fromTask) !== String(empId)
  ) {
    return String(fromTask)
  }
  if (nameMap && empId != null) {
    const mapped = nameMap[String(empId)]
    if (mapped) return String(mapped)
  }
  const nested = task.assignee || task.assignedToUser || task.employee
  if (nested && typeof nested === 'object') {
    const n = nested.name
      || nested.fullName
      || nested.title
      || `${nested.first_name || nested.firstName || ''} ${nested.last_name || nested.lastName || ''}`.trim()
    if (n) return String(n)
  }
  if (fromTask && fromTask !== '-' && fromTask !== 'Unassigned') return String(fromTask)
  return null
}

/** Build empId → display name from a task list (fills gaps across rows). */
function buildAssigneeNameMap(tasks = []) {
  const map = {}
  ;(tasks || []).forEach(task => {
    const empId = taskAssigneeEmpId(task)
    if (empId == null || map[empId]) return
    const name = resolveAssigneeDisplayName(task)
    if (name) map[empId] = name
  })
  return map
}

function buildTaskPingNotification(task, ping, { nameMap = null, role = 'assignee' } = {}) {
  const id = getTaskId(task)
  const when = ping.lastPingedAt || new Date().toISOString()
  const who = ping.managerName || 'Your manager'
  const title = task && task.title ? task.title : (ping.taskTitle || 'a task')
  const count = Number(ping.pingCount) || 1
  const assigneeName = resolveAssigneeDisplayName(task, nameMap)
  const isManager = role === 'creator' || role === 'manager'
  let message
  if (isManager) {
    const whoAssigned = assigneeName || 'your report'
    message = count > 1
      ? `"${title}" assigned to ${whoAssigned} was pinged for an update (×${count})`
      : `"${title}" assigned to ${whoAssigned} was pinged for an update`
  } else {
    message = count > 1
      ? `${who} asked for an update on "${title}" (×${count})`
      : `${who} asked for an update on "${title}"`
  }
  const dedupeKey = `task_ping:${id}:${when}${isManager ? ':manager' : ''}`
  return {
    id: `task-ping:${id}:${when}${isManager ? ':manager' : ''}`,
    type: 'task_ping',
    title: isManager ? 'Team task update requested' : 'Task update requested',
    message,
    createdAt: when,
    read: false,
    link: { name: 'apps-tasks' },
    linkQuery: id != null ? { taskId: String(id) } : null,
    icon: 'BellIcon',
    variant: 'light-warning',
    source: 'task_ping',
    meta: {
      taskId: id,
      empId: ping.empId || taskAssigneeEmpId(task),
      assigneeName: assigneeName || null,
      managerName: who,
      managerEmpId: ping.managerEmpId,
      lastPingedAt: when,
      pingCount: count,
      role: isManager ? 'creator' : 'assignee',
      dedupeKey,
    },
  }
}

function buildEmployeePingNotification(empId, ping) {
  const when = ping.lastPingedAt || new Date().toISOString()
  const who = ping.managerName || 'Your manager'
  const count = Number(ping.pingCount) || 1
  const dedupeKey = `task_ping:employee:${empId}:${when}`
  return {
    id: `task-ping:employee:${empId}:${when}`,
    type: 'task_ping',
    title: 'Manager ping',
    message: count > 1
      ? `${who} pinged you — please review your tasks (×${count})`
      : `${who} pinged you — please review and update your tasks`,
    createdAt: when,
    read: false,
    link: { name: 'apps-tasks' },
    linkQuery: null,
    icon: 'BellIcon',
    variant: 'light-warning',
    source: 'task_ping',
    meta: {
      empId,
      managerName: who,
      managerEmpId: ping.managerEmpId,
      lastPingedAt: when,
      pingCount: count,
      employeeLevel: true,
      dedupeKey,
    },
  }
}

function buildEscalationNotification(task, level, role, nameMap = null) {
  const id = getTaskId(task)
  const meta = escalationLevelMeta(level) || { label: level, variant: 'light-danger' }
  const title = (task && task.title) || 'a task'
  const dueDate = (task && task.dueDate) || ''
  const when = dueDate
    ? `${dueDate}T12:00:00.000Z`
    : (task.updatedAt || task.createdAt || new Date().toISOString())
  const isAssignee = role === 'assignee'
  const assigneeName = resolveAssigneeDisplayName(task, nameMap)
  let message
  if (isAssignee) {
    message = `"${title}" reached ${meta.label}`
  } else if (assigneeName) {
    message = `"${title}" assigned to ${assigneeName} reached ${meta.label}`
  } else {
    message = `"${title}" (assigned to your report) reached ${meta.label}`
  }
  const dedupeKey = `task_escalation:${id}:${level}:${dueDate}:${role}`
  return {
    id: `task-escalation:${id}:${level}:${dueDate}:${role}`,
    type: 'task_escalation',
    title: isAssignee ? 'Task escalated' : 'Team task escalated',
    message,
    createdAt: when,
    read: false,
    link: { name: 'apps-tasks' },
    linkQuery: id != null ? { taskId: String(id) } : null,
    icon: 'AlertTriangleIcon',
    variant: meta.variant || 'light-danger',
    source: 'task_escalation',
    meta: {
      taskId: id,
      level,
      dueDate,
      role,
      assigneeName: assigneeName || null,
      assigneeEmpId: taskAssigneeEmpId(task),
      dedupeKey,
    },
  }
}

/**
 * Build inbox items from tasks assigned to this employee that carry an
 * active ping (fields or changeHistory). Does not consult toast "seen".
 */
export function synthesizeTaskPingNotifications(empId, tasks = []) {
  if (empId == null) return []
  const empKey = String(empId)
  const items = []
  const seenIds = new Set()
  const nameMap = buildAssigneeNameMap(tasks)

  ;(tasks || []).forEach(task => {
    if (isTaskComplete(task)) return
    const assignee = taskAssigneeEmpId(task)
    if (assignee == null || String(assignee) !== empKey) return
    const ping = getTaskPing(task)
    if (!ping || !ping.lastPingedAt) return
    const n = buildTaskPingNotification(task, { ...ping, empId: empKey }, { nameMap, role: 'assignee' })
    seenIds.add(n.id)
    items.push(n)
  })

  const employeePing = getEmployeePing(empId)
  if (employeePing && employeePing.lastPingedAt) {
    const n = buildEmployeePingNotification(empId, employeePing)
    if (!seenIds.has(n.id)) items.push(n)
  }

  return items
}

/**
 * Escalation inbox items for the assignee (and optionally the creating
 * manager) so overdue tiers are not silent in the navbar bell.
 */
export function synthesizeTaskEscalationNotifications(empId, tasks = []) {
  if (empId == null) return []
  const empKey = String(empId)
  const items = []
  const seenIds = new Set()
  const nameMap = buildAssigneeNameMap(tasks)

  ;(tasks || []).forEach(task => {
    if (isTaskComplete(task)) return
    // Recurring occurrences get gentle reminders, not escalation inbox items.
    if (taskRecurringTaskId(task) != null) return
    const level = getEscalationLevel(task)
    if (!level) return
    const assignee = taskAssigneeEmpId(task)
    const owner = taskOwnerEmpId(task)

    if (assignee != null && String(assignee) === empKey) {
      const n = buildEscalationNotification(task, level, 'assignee', nameMap)
      if (!seenIds.has(n.id)) {
        seenIds.add(n.id)
        items.push(n)
      }
    }
    if (owner != null && String(owner) === empKey && String(owner) !== String(assignee)) {
      const n = buildEscalationNotification(task, level, 'creator', nameMap)
      if (!seenIds.has(n.id)) {
        seenIds.add(n.id)
        items.push(n)
      }
    }
  })

  return items
}

function buildReminderNotification(task, role, nameMap = null) {
  const id = getTaskId(task)
  const title = (task && task.title) || 'a task'
  const dueDate = (task && task.dueDate) || ''
  const when = dueDate
    ? `${dueDate}T12:00:00.000Z`
    : (task.updatedAt || task.createdAt || new Date().toISOString())
  const isAssignee = role === 'assignee'
  const assigneeName = resolveAssigneeDisplayName(task, nameMap)
  let message
  if (isAssignee) {
    message = `Reminder: "${title}" is due (recurring task)`
  } else if (assigneeName) {
    message = `Reminder: "${title}" for ${assigneeName} is due (recurring task)`
  } else {
    message = `Reminder: recurring task "${title}" is due`
  }
  const dedupeKey = `task_reminder:${id}:${dueDate}:${role}`
  return {
    id: `task-reminder:${id}:${dueDate}:${role}`,
    type: 'task_reminder',
    title: isAssignee ? 'Task reminder' : 'Team task reminder',
    message,
    createdAt: when,
    read: false,
    link: { name: 'apps-tasks' },
    linkQuery: id != null ? { taskId: String(id) } : null,
    icon: 'ClockIcon',
    variant: 'light-info',
    source: 'task_reminder',
    meta: {
      taskId: id,
      dueDate,
      role,
      assigneeName: assigneeName || null,
      assigneeEmpId: taskAssigneeEmpId(task),
      dedupeKey,
    },
  }
}

/** Inbox items for recurring-task occurrences due today only — no escalation tiers. */
export function synthesizeTaskReminderNotifications(empId, tasks = []) {
  if (empId == null) return []
  const empKey = String(empId)
  const items = []
  const seenIds = new Set()
  const nameMap = buildAssigneeNameMap(tasks)

  ;(tasks || []).forEach(task => {
    if (isTaskComplete(task)) return
    if (taskRecurringTaskId(task) == null) return
    if (!taskEligibleForRecurringReminder(task)) return
    const assignee = taskAssigneeEmpId(task)
    const owner = taskOwnerEmpId(task)

    if (assignee != null && String(assignee) === empKey) {
      const n = buildReminderNotification(task, 'assignee', nameMap)
      if (!seenIds.has(n.id)) {
        seenIds.add(n.id)
        items.push(n)
      }
    }
    if (owner != null && String(owner) === empKey && String(owner) !== String(assignee)) {
      const n = buildReminderNotification(task, 'creator', nameMap)
      if (!seenIds.has(n.id)) {
        seenIds.add(n.id)
        items.push(n)
      }
    }
  })

  return items
}

function filterInboxForDisplay(items) {
  const list = (items || []).filter(n => (
    n && !isStaleRecurringTaskReminderNotification(n)
  ))
  const allowTaskMgmt = canAccessTaskManagement()
  return list.filter(n => {
    if (!allowTaskMgmt && isTaskManagementPush(n.type, n.module || n.moduleName)) {
      return false
    }
    if (FCM_PUSH_ONLY && !isInAppInboxEnabledForType(n.type)) {
      return false
    }
    // Hide assignment / escalation / ping / reminder for tasks already Done.
    // FINAL here means escalation tier (overdue), not completion — open
    // FINAL escalations still show; completed-task rows are suppressed.
    if (shouldHideCompletedTaskNotification(n)) {
      return false
    }
    return true
  })
}

function synthesizeFromTasks(empId, tasks = []) {
  const items = [
    ...synthesizeTaskPingNotifications(empId, tasks),
    ...synthesizeTaskEscalationNotifications(empId, tasks),
    ...synthesizeTaskReminderNotifications(empId, tasks),
  ]
  if (!FCM_PUSH_ONLY) return items
  return items.filter(n => isInAppInboxEnabledForType(n.type))
}

function serializeLink(link, linkQuery) {
  if (link == null) return null
  if (typeof link === 'string') {
    if (linkQuery && typeof linkQuery === 'object') {
      const qs = new URLSearchParams(linkQuery).toString()
      if (!qs) return link
      return link.includes('?') ? `${link}&${qs}` : `${link}?${qs}`
    }
    return link
  }
  if (typeof link === 'object' && link.name) {
    // Route-object → path guess for storage; client still uses linkQuery locally.
    if (link.name === 'apps-tasks') {
      const tid = linkQuery && linkQuery.taskId
      return tid != null ? `/apps/tasks?taskId=${encodeURIComponent(tid)}` : '/apps/tasks'
    }
    if (link.name === 'apps-call-analytics') {
      const dir = linkQuery && linkQuery.direction
      return dir
        ? `/apps/call-analytics?direction=${encodeURIComponent(dir)}`
        : '/apps/call-analytics'
    }
    return null
  }
  return null
}

function itemDedupeKey(item) {
  if (!item) return null
  if (item.meta && item.meta.dedupeKey) return String(item.meta.dedupeKey)
  if (item.id) return String(item.id)
  return null
}

function apiItemsCoverLocal(apiItems, localItem) {
  if (!localItem) return false
  const key = itemDedupeKey(localItem)
  if (key && apiItems.some(a => itemDedupeKey(a) === key)) return true
  // Soft match: same type + taskId (+ level for escalations)
  const lt = localItem.type
  const lTask = localItem.meta && localItem.meta.taskId != null
    ? String(localItem.meta.taskId)
    : null
  const lLevel = localItem.meta && localItem.meta.level
  return apiItems.some(a => {
    if (!a || a.type !== lt) return false
    const aTask = a.meta && a.meta.taskId != null ? String(a.meta.taskId) : null
    if (lTask && aTask && lTask === aTask) {
      if (lt === 'task_escalation') {
        return !lLevel || (a.meta && a.meta.level === lLevel)
      }
      if (lt === 'task_ping') {
        const lWhen = localItem.meta && localItem.meta.lastPingedAt
        const aWhen = a.meta && a.meta.lastPingedAt
        if (lWhen && aWhen) return String(lWhen) === String(aWhen)
        return true
      }
      return true
    }
    return false
  })
}

async function postCreateNotification(payload) {
  const base = apiWriteBase()
  if (!base || !payload) return null
  const dedupeKey = payload.dedupeKey || (payload.meta && payload.meta.dedupeKey) || null
  if (dedupeKey && postedDedupeKeys.has(String(dedupeKey))) return null
  if (dedupeKey) postedDedupeKeys.add(String(dedupeKey))

  try {
    const res = await axios.post(API_URL + base, payload, {
      headers: AuthService.authHeader(),
      validateStatus: status => status < 500,
      timeout: 8000,
    })
    if (res.status >= 200 && res.status < 300) {
      return extractObjectPayload(res.data) || res.data
    }
    // Allow retry later if server rejected (auth / validation).
    if (dedupeKey) postedDedupeKeys.delete(String(dedupeKey))
  } catch (e) {
    if (dedupeKey) postedDedupeKeys.delete(String(dedupeKey))
    softWarnNetwork('[notifications] POST create failed', e)
  }
  return null
}

function toCreatePayload(item, empId) {
  if (!item) return null
  const dedupeKey = itemDedupeKey(item)
  return {
    empId: empId != null ? String(empId) : undefined,
    userId: readUserId(),
    type: item.type || 'general',
    title: item.title || 'Notification',
    message: item.message || '',
    link: serializeLink(item.link, item.linkQuery),
    meta: {
      ...(item.meta || {}),
      clientId: item.id,
      source: item.source || 'client',
    },
    dedupeKey: dedupeKey || undefined,
  }
}

/**
 * POST any synthesized / local items that are not already represented on
 * the server. Bridge until backend creates rows on domain events.
 * @returns {number} how many POSTs succeeded
 */
async function syncMissingToApi(empId, localItems, apiItems) {
  if (!apiWriteBase()) return 0
  const eligible = (localItems || []).filter(n => isInAppInboxEnabledForType(n.type))
  const missing = eligible.filter(n => !apiItemsCoverLocal(apiItems, n))
  let posted = 0
  for (let i = 0; i < missing.length; i += 1) {
    const payload = toCreatePayload(missing[i], empId)
    if (!payload) continue
    // eslint-disable-next-line no-await-in-loop
    const created = await postCreateNotification(payload)
    if (created) posted += 1
  }
  return posted
}

async function fetchApiList(empId) {
  if (apiAuthBlocked) return { path: null, items: null, ok: false }
  const path = await probeApiList()
  if (!path) return { path: null, items: null, ok: false }

  try {
    const res = await axios.get(API_URL + path, {
      headers: AuthService.authHeader(),
      params: empId != null ? { empId } : undefined,
      validateStatus: status => status < 500,
      timeout: 8000,
    })
    if (res.status === 401 || res.status === 403) {
      softWarnNetwork('[notifications] API list failed', { response: { status: res.status } })
      markNotificationsAuthBlocked()
      return { path: null, items: null, ok: false }
    }
    if (res.status !== 200) {
      return { path, items: null, ok: false }
    }
    const rows = extractArrayPayload(res.data)
    if (rows == null) return { path, items: null, ok: false }
    return {
      path,
      items: rows.map(normalizeApiItem).filter(Boolean),
      ok: true,
    }
  } catch (e) {
    softWarnNetwork('[notifications] API list failed', e)
    if (e?.response?.status === 401 || e?.response?.status === 403) {
      markNotificationsAuthBlocked()
    }
    return { path: null, items: null, ok: false }
  }
}

async function fetchViewerTasks(viewer) {
  try {
    const res = await taskService.getTasks({ scope: 'mine', empId: viewer })
    const authStatus = res?.httpStatus || res?.status
    if (!res || res.status === 'SERVICE_UNAVAILABLE' || authStatus === 401 || authStatus === 403) {
      return null
    }
    const raw = Array.isArray(res) ? res : (Array.isArray(res.data) ? res.data : [])
    return raw.filter(t => {
      const assignee = taskAssigneeEmpId(t)
      const owner = taskOwnerEmpId(t)
      return (assignee != null && String(assignee) === String(viewer))
        || (owner != null && String(owner) === String(viewer))
    })
  } catch (e) {
    return null
  }
}

class NotificationService {
  /** Whether a backend list endpoint was found this session (null = not probed). */
  get apiAvailable() {
    if (!apiProbeDone) return null
    return !!apiListPath
  }

  /**
   * Load inbox from API (server truth). On API failure, soft-fail with
   * task synthesis for this session only — never restore a persisted cache.
   * When API works, POST any synthesized rows that are not yet on the server
   * (bridge), then refresh list if anything posted.
   */
  async loadInbox({ empId, tasks } = {}) {
    const viewer = empId != null ? empId : readUserEmpId()
    if (viewer == null) return []

    const apiResult = await fetchApiList(viewer)
    let apiItems = apiResult.ok ? (apiResult.items || []) : null

    let taskRows = Array.isArray(tasks) ? tasks : null
    if (!taskRows) {
      taskRows = await fetchViewerTasks(viewer)
    }

    if (Array.isArray(taskRows)) {
      rememberTaskCompletionState(taskRows)
    }

    const synthesized = Array.isArray(taskRows)
      ? synthesizeFromTasks(viewer, taskRows)
      : []

    if (apiResult.ok && Array.isArray(apiItems)) {
      // Server truth for display.
      replaceFromApi(filterInboxForDisplay(apiItems), { empId: viewer, mode: 'replace' })

      if (synthesized.length) {
        const posted = await syncMissingToApi(viewer, synthesized, apiItems)
        if (posted > 0) {
          // Re-fetch so newly POSTed rows appear with server ids.
          const refreshed = await fetchApiList(viewer)
          if (refreshed.ok && Array.isArray(refreshed.items)) {
            replaceFromApi(filterInboxForDisplay(refreshed.items), { empId: viewer, mode: 'replace' })
          }
        }
      }
      return filterInboxForDisplay(listNotifications(viewer))
    }

    // Soft-fail: empty or session synthesis only — do not keep a stale inbox.
    replaceFromApi([], { empId: viewer, mode: 'replace' })
    if (synthesized.length) {
      upsertNotifications(synthesized, { empId: viewer })
    }
    return filterInboxForDisplay(listNotifications(viewer))
  }

  /**
   * Ingest task pings + escalations from an already-fetched task list
   * (used by the global poller). When API is available, POST missing rows
   * and refresh from server; otherwise session in-memory upsert only.
   */
  async ingestTaskPings(empId, tasks = []) {
    const viewer = empId != null ? empId : readUserEmpId()
    if (viewer == null) return filterInboxForDisplay(listNotifications())

    rememberTaskCompletionState(tasks)
    const synthesized = synthesizeFromTasks(viewer, tasks)

    // Ensure probe has run (may be first call after login).
    await probeApiList()

    if (apiListPath) {
      const apiResult = await fetchApiList(viewer)
      if (apiResult.ok && Array.isArray(apiResult.items)) {
        let items = apiResult.items
        if (synthesized.length) {
          const posted = await syncMissingToApi(viewer, synthesized, apiResult.items)
          if (posted > 0) {
            const refreshed = await fetchApiList(viewer)
            if (refreshed.ok && Array.isArray(refreshed.items)) {
              items = refreshed.items
            }
          }
        }
        replaceFromApi(filterInboxForDisplay(items), { empId: viewer, mode: 'replace' })
        return filterInboxForDisplay(listNotifications(viewer))
      }
    }

    if (synthesized.length) {
      upsertNotifications(synthesized, { empId: viewer })
    }
    return filterInboxForDisplay(listNotifications(viewer))
  }

  list(empId) {
    return filterInboxForDisplay(listNotifications(empId))
  }

  /**
   * Inbox-only upsert for incoming FCM (no POST — push already originated server-side
   * or from another user's client fan-out).
   */
  ingestFcm(notification, { empId, forceUnread = true } = {}) {
    const viewer = empId != null ? empId : readUserEmpId()
    if (!notification) return filterInboxForDisplay(listNotifications())
    if (viewer == null) {
      // eslint-disable-next-line no-console
      console.warn('[notifications] ingestFcm skipped — no emp_id in userData')
      return filterInboxForDisplay(listNotifications())
    }
    if (!isInAppInboxEnabledForType(notification.type)) {
      return filterInboxForDisplay(listNotifications(viewer))
    }
    if (shouldHideCompletedTaskNotification(notification)) {
      return filterInboxForDisplay(listNotifications(viewer))
    }
    upsertNotification(notification, { empId: viewer, forceUnread })
    return filterInboxForDisplay(listNotifications(viewer))
  }

  /**
   * Mark a task completed for bell filtering and re-list the session inbox
   * (hides stale assignment/escalation rows for that task id immediately).
   */
  noteTaskCompleted(taskOrId, { empId } = {}) {
    noteTaskCompletedForInbox(taskOrId)
    const viewer = empId != null ? empId : readUserEmpId()
    return filterInboxForDisplay(listNotifications(viewer))
  }

  /**
   * Create / upsert a notification for the current user (or specified empId).
   * Writes session in-memory inbox; POSTs to API when available.
   */
  async create(notification, { empId, forceUnread = true } = {}) {
    const viewer = empId != null ? empId : readUserEmpId()
    if (viewer == null || !notification) return filterInboxForDisplay(listNotifications())
    if (!isInAppInboxEnabledForType(notification.type)) {
      return filterInboxForDisplay(listNotifications(viewer))
    }
    if (shouldHideCompletedTaskNotification(notification)) {
      return filterInboxForDisplay(listNotifications(viewer))
    }

    const withMeta = {
      ...notification,
      meta: {
        ...(notification.meta || {}),
        dedupeKey: (notification.meta && notification.meta.dedupeKey) || notification.id,
      },
    }
    upsertNotification(withMeta, { empId: viewer, forceUnread })

    await probeApiList()
    if (apiListPath) {
      const created = await postCreateNotification(toCreatePayload(withMeta, viewer))
      if (created) {
        const normalized = normalizeApiItem(created)
        if (normalized) {
          upsertNotification(normalized, { empId: viewer, forceUnread })
        }
      }
    }
    return filterInboxForDisplay(listNotifications(viewer))
  }

  async markRead(id, empId) {
    const items = filterInboxForDisplay(markNotificationRead(id, { empId }))
    if (!apiListPath) await probeApiList()
    if (apiListPath && id && String(id).startsWith('api:')) {
      const rawId = String(id).slice(4)
      const base = apiWriteBase()
      const candidates = [
        `${base}/${rawId}/read`,
        `${base}/${rawId}`,
        `notifications/${rawId}/read`,
      ]
      for (let i = 0; i < candidates.length; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await axios.patch(API_URL + candidates[i], { read: true }, {
            headers: AuthService.authHeader(),
            validateStatus: status => status < 500,
          })
          if (res.status >= 200 && res.status < 300) break
        } catch (e) {
          // try next
        }
      }
    }
    return filterInboxForDisplay(items)
  }

  async markAllRead(empId) {
    const items = filterInboxForDisplay(markAllNotificationsRead({ empId }))
    if (!apiListPath) await probeApiList()
    const base = apiWriteBase()
    if (base) {
      const candidates = [
        `${base}/read-all`,
        'notifications/read-all',
      ]
      for (let i = 0; i < candidates.length; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await axios.patch(API_URL + candidates[i], { read: true }, {
            headers: AuthService.authHeader(),
            validateStatus: status => status < 500,
          })
          if (res.status >= 200 && res.status < 300) break
        } catch (e) {
          // try next
        }
      }
    }
    return filterInboxForDisplay(items)
  }

  /** Dismiss / clear every notification for the viewer (API + session memory). */
  async clearAll(empId) {
    const items = filterInboxForDisplay(clearAllNotifications({ empId }))
    if (!apiListPath) await probeApiList()
    const base = apiWriteBase()
    if (base) {
      const attempts = [
        { method: 'post', path: `${base}/clear` },
        { method: 'patch', path: `${base}/clear-all` },
        { method: 'delete', path: base },
      ]
      for (let i = 0; i < attempts.length; i += 1) {
        const { method, path } = attempts[i]
        try {
          // eslint-disable-next-line no-await-in-loop
          const res = await axios({
            method,
            url: API_URL + path,
            headers: AuthService.authHeader(),
            validateStatus: status => status < 500,
            data: method === 'delete' ? undefined : {},
          })
          if (res.status >= 200 && res.status < 300) break
        } catch (e) {
          // try next
        }
      }
    }
    return filterInboxForDisplay(items)
  }
}

export default new NotificationService()
